import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { useEffect } from 'react'
import { CognitoAuthResponse, LoginResponse } from './type/authentication'
import { AuthorizationHandler } from './authorizationHandler'
import i18n from '../i18n'

export enum APIStatus {
  IDLE,
  PENDING,
  REJECTED,
  FULFILLED,
}

export type APIError = {
  message: string
}

export type APIData<DataType = any> = {
  status: APIStatus
  error?: APIError
  data?: DataType
}

export const InternalError: APIError = {
  message: 'Generic error during request.',
}

export const UnhandledError: APIError = {
  message: 'Cannot handle error data.',
}

/**
 * Handle the differents states of an api response ( fulfilled, rejected or pending ).
 *
 * @param response response object to handle
 * @param handlers object that contains the different callbacks to call for each state.
 *
 * @note If the handlers object was declared inline you must wrap it inside useMemo() callback
 *  otherwise the current state callback will be fired for each rendering cycle.
 * @see https://reactjs.org/docs/hooks-reference.html#useMemo
 */

export const useAPIData = <DataType>(
  response: APIData<DataType>,
  handlers: {
    onFulfilled?: (data: DataType) => void
    onRejected?: (error: APIError) => void
    onPending?: () => void
  },
) => {
  const { onFulfilled, onRejected, onPending } = handlers

  useEffect(() => {
    if (response.status === APIStatus.REJECTED && onRejected) {
      onRejected(response.error || UnhandledError)
    }
  }, [response.status, response.error, onRejected])

  useEffect(() => {
    if (response.status === APIStatus.FULFILLED && onFulfilled) {
      onFulfilled(response.data!)
    }
  }, [response.status, response.data, onFulfilled])

  useEffect(() => {
    if (response.status === APIStatus.PENDING && onPending) {
      onPending()
    }
  }, [response.status, onPending])
}

export const getExceptionPayload = (ex: unknown, fallback?: string, options?: object): APIError => {
  console.warn('Exception:', ex)

  if (ex instanceof AxiosError) {
    const i18nKey = `general:api.error.${ex.response?.data.message}`

    if (i18n.exists(i18nKey)) {
      return { message: i18n.t(i18nKey) }
    }
  }

  return !fallback ? InternalError : { message: !options ? i18n.t(fallback) : i18n.t(fallback, options) }
}

export const onFulfilledRequest = (response: AxiosResponse) => response

export const onRejectedResponse = (error: any): any => {
  if (error.response?.status === 401) {
    AuthorizationHandler.clearSession()
  }

  return Promise.reject(error.response?.data?.error ? error.response.data.error : InternalError)
}

export const authenticatedRequest = async (): Promise<AxiosInstance> => {
  if (!AuthorizationHandler.idToken) {
    if (!AuthorizationHandler.refreshToken) {
      throw new Error('refresh token expired')
    }

    const params = new URLSearchParams()
    params.append('grant_type', 'refresh_token')
    params.append('client_id', process.env.REACT_APP_CLIENT_ID!)
    params.append('client_secret', process.env.REACT_APP_CLIENT_SECRET!)
    params.append('refresh_token', AuthorizationHandler.refreshToken)

    const response = await axios.post<CognitoAuthResponse>(
      `${process.env.REACT_APP_COGNITO_URL}/oauth2/token`,
      params,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    )

    if (response.data.error) throw new Error(response.data.errorDescription)

    AuthorizationHandler.initSession(response.data as LoginResponse)
  }

  const instance = axios.create({
    baseURL: `${process.env.REACT_APP_ROOT_URL}`,
    headers: {
      Authorization: `Bearer ${AuthorizationHandler.idToken}`,
    },
  })

  return instance
}

//Se va a buon fine chiama la onFullfilled e viceversa
// unauthenticatedRequest.interceptors.response.use(onFulfilledRequest, onRejectedResponse)
